import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, X, Plus } from 'lucide-react';
import DocumentUpload from '../../common/DocumentUpload';
import { FormService } from '../../../services/patientmr/forms.service';
import type { FormRecordDto, FormDocumentDto } from '../../../dtos/patientmr/forms.dto';
import SubmitterCertificationSection from './SubmitterCertificationSection';
import { useAuth } from '../../../context/authcontext';
import { F00028_PPECC_HOURS_LABEL, F00028_PPECC_TIME_ZONES, ppeccTimeZoneFromAgency } from '../../../constants/tmhpFormText';
import PrintedNameField from './PrintedNameField';

interface HomeHealthPOCFormProps {
  isReadOnly?: boolean;
  initialData?: Record<string, any>;
  onDataChange?: (data: any) => void;
  patientId?: string;
  formRecord?: FormRecordDto;
  onFormRecordChange?: (record: FormRecordDto) => void;
}

interface DMEItem {
  description: string;
  own: boolean;
  repair: boolean;
  buy: boolean;
  rent: boolean;
  howLong: string;
}

const emptyDMEItem = (): DMEItem => ({ description: '', own: false, repair: false, buy: false, rent: false, howLong: '' });

const CollapsibleSection: React.FC<{
  title: string; sectionKey: string;
  collapsed: boolean; onToggle: () => void;
  children: React.ReactNode; colorClass?: string;
}> = ({ title, collapsed, onToggle, children, colorClass = 'border-slate-200 bg-slate-50' }) => (
  <div className={`rounded-lg border ${colorClass} shadow-sm mb-4`}>
    <div className="flex items-center justify-between cursor-pointer px-4 py-3 hover:bg-slate-100 rounded-t-lg transition-colors" onClick={onToggle}>
      <h4 className="text-base font-semibold text-gray-900 text-left">{title}</h4>
      {collapsed ? <ChevronDown size={16} className="text-gray-500 shrink-0" /> : <ChevronUp size={16} className="text-gray-500 shrink-0" />}
    </div>
    <div className={`px-4 pb-4 pt-2 ${collapsed ? 'hidden' : 'block'}`}>{children}</div>
  </div>
);

const RequiredMark: React.FC = () => <span className="text-red-500 ml-0.5">*</span>;

const inputClass =
  'w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500 text-sm text-left';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1 text-left';

const HomeHealthPOCForm: React.FC<HomeHealthPOCFormProps> = ({ isReadOnly = false, initialData = {}, onDataChange, patientId, formRecord, onFormRecordChange }) => {
  const { agencyTimezone } = useAuth();
  const [form, setForm] = useState<Record<string, any>>({
    clientName: '',
    dob: '',
    dateLastSeen: '',
    medicaidNumber: '',
    hhaProviderName: '',
    hhaPhone: '',
    hhaFax: '',
    hhaTaxId: '',
    hhaAddress: '',
    hhaCity: '',
    hhaState: '',
    hhaZip: '',
    hhaNpi: '',
    hhaTaxonomy: '',
    hhaBenefitCode: '',
    ppeccProviderName: '',
    ppeccPhone: '',
    ppeccFax: '',
    ppeccNpi: '',
    ppeccAddress: '',
    ppeccCity: '',
    ppeccState: '',
    ppeccZip: '',
    ppeccOpen: '',
    ppeccClose: '',
    // F00028 "Central Time / Mountain Time" box; new forms default from the agency time zone
    // (America/Chicago → Central, America/Denver → Mountain), a saved choice wins.
    ppeccTimezone: ppeccTimeZoneFromAgency(initialData.agencyTimezone ?? agencyTimezone),
    physicianProviderName: '',
    physicianPhone: '',
    physicianNpi: '',
    physicianLicense: '',
    pocStatus: '',
    originalSOCDate: '',
    revisedEffectiveDate: '',
    otherAgencyServices: '',
    diagnoses: '',
    functionalLimitations: '',
    medications: '',
    dietOrdered: '',
    mentalStatus: '',
    prognosis: '',
    rehabilitationPotential: '',
    safetyPrecautions: '',
    medicalNecessity: '',
    hcpcsRequested: '',
    dosFrom: '',
    dosTo: '',
    snVisits: '',
    hhaVisits: '',
    supplies: '',
    dmeItems: [] as DMEItem[],
    rnSignature: '',
    rnDateSigned: '',
    authFrom: '',
    authTo: '',
    conflictOfInterest: false,
    exception: false,
    physicianSignature: '',
    physicianDateSigned: '',
    ...initialData,
  });

  useEffect(() => {
    onDataChange?.(form);
  }, [form, onDataChange]);

  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const toggleSection = (k: string) => setCollapsedSections(p => ({ ...p, [k]: !p[k] }));

  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);


  const handleFilesSelected = (files: File[]) => {
    if (formRecord?.status !== 'COMPLETE') {
      setActionError('Document upload is only available when the form is in Complete status.');
      return;
    }
    setActionError(null);
    setStagedFiles(prev => [...prev, ...files]);
  };

  const handleRemoveStagedFile = (index: number) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadStagedFiles = async () => {
    if (!stagedFiles.length || !patientId || !formRecord) return;
    setActionError(null);
    setUploading(true);
    try {
      let updatedForm = formRecord;
      for (const file of stagedFiles) {
        updatedForm = await FormService.addDocument(patientId, formRecord.formId, file);
      }
      setStagedFiles([]);
      onFormRecordChange?.(updatedForm);
    } catch (err) {
      console.error('Upload failed:', err);
      setActionError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = async (doc: FormDocumentDto) => {
    if (!patientId || !formRecord) return;
    setActionError(null);
    try {
      const result = await FormService.getDocumentUrl(patientId, formRecord.formId, doc.id);
      window.open(result.url, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
      setActionError(err instanceof Error ? err.message : 'Download failed. Please try again.');
    }
  };

  const handleDeleteDocument = async (doc: FormDocumentDto) => {
    if (!patientId || !formRecord) return;
    setActionError(null);
    try {
      setDeletingDocId(doc.id);
      const updatedForm = await FormService.deleteDocument(patientId, formRecord.formId, doc.id);
      onFormRecordChange?.(updatedForm);
    } catch (err) {
      console.error('Delete failed:', err);
      setActionError(err instanceof Error ? err.message : 'Delete failed. Please try again.');
    } finally {
      setDeletingDocId(null);
    }
  };

  const set = (field: string, value: any) => {
    if (isReadOnly) return;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const setDME = (index: number, field: keyof DMEItem, value: any) => {
    if (isReadOnly) return;
    setForm(prev => {
      const items = [...(prev.dmeItems as DMEItem[])];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, dmeItems: items };
    });
  };

  const addDMEItem = () => {
    if (isReadOnly) return;
    setForm(prev => ({ ...prev, dmeItems: [...(prev.dmeItems as DMEItem[]), emptyDMEItem()] }));
  };

  const removeDMEItem = (idx: number) => {
    if (isReadOnly) return;
    setForm(prev => {
      const items = (prev.dmeItems as DMEItem[]).filter((_, i) => i !== idx);
      return { ...prev, dmeItems: items };
    });
  };

  return (
    <div className="space-y-6 text-left">
      <div className="mb-4">
        <p className="text-xs text-gray-500 uppercase tracking-wide text-left">Form Code: F00028</p>
        <p className="text-sm text-gray-600 text-left">Home Health Plan of Care</p>
      </div>

      <SubmitterCertificationSection formCode="F00028" data={form} onChange={set} isReadOnly={isReadOnly} />

      {/* Section A */}
      <CollapsibleSection title="Section A: Client Information" sectionKey="client-info" collapsed={!!collapsedSections['client-info']} onToggle={() => toggleSection('client-info')} colorClass="border-blue-200 bg-blue-50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Client Name<RequiredMark /></label>
            <input type="text" value={form.clientName} onChange={e => set('clientName', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Date of Birth<RequiredMark /></label>
            <input type="date" value={form.dob} onChange={e => set('dob', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Date Last Seen By Physician or Allowed Practitioner</label>
            <input type="date" value={form.dateLastSeen} onChange={e => set('dateLastSeen', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Medicaid Number<RequiredMark /></label>
            <input type="text" value={form.medicaidNumber} onChange={e => set('medicaidNumber', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>
      </CollapsibleSection>

      {/* Section B */}
      <CollapsibleSection title="Section B: Rendering Provider Home Health Agency (HHA) Information" sectionKey="hha-info" collapsed={!!collapsedSections['hha-info']} onToggle={() => toggleSection('hha-info')} colorClass="border-indigo-200 bg-indigo-50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Provider Name<RequiredMark /></label>
            <input type="text" value={form.hhaProviderName} onChange={e => set('hhaProviderName', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Telephone</label>
            <input type="tel" value={form.hhaPhone} onChange={e => set('hhaPhone', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Fax</label>
            <input type="tel" value={form.hhaFax} onChange={e => set('hhaFax', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Tax ID<RequiredMark /></label>
            <input type="text" value={form.hhaTaxId} onChange={e => set('hhaTaxId', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Street Address<RequiredMark /></label>
            <input type="text" value={form.hhaAddress} onChange={e => set('hhaAddress', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>City<RequiredMark /></label>
            <input type="text" value={form.hhaCity} onChange={e => set('hhaCity', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelClass}>State<RequiredMark /></label>
              <input type="text" maxLength={2} value={form.hhaState} onChange={e => set('hhaState', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>ZIP+4<RequiredMark /></label>
              <input type="text" value={form.hhaZip} onChange={e => set('hhaZip', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
          </div>
          <div>
            <label className={labelClass}>NPI<RequiredMark /></label>
            <input type="text" value={form.hhaNpi} onChange={e => set('hhaNpi', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Taxonomy<RequiredMark /></label>
            <input type="text" value={form.hhaTaxonomy} onChange={e => set('hhaTaxonomy', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Benefit Code<RequiredMark /></label>
            <input type="text" value={form.hhaBenefitCode} onChange={e => set('hhaBenefitCode', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>
      </CollapsibleSection>

      {/* Section C */}
      <CollapsibleSection title="Section C: PPECC Provider Information" sectionKey="ppecc-info" collapsed={!!collapsedSections['ppecc-info']} onToggle={() => toggleSection('ppecc-info')} colorClass="border-violet-200 bg-violet-50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>PPECC Provider Name</label>
            <input type="text" value={form.ppeccProviderName} onChange={e => set('ppeccProviderName', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Telephone</label>
            <input type="tel" value={form.ppeccPhone} onChange={e => set('ppeccPhone', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Fax</label>
            <input type="tel" value={form.ppeccFax} onChange={e => set('ppeccFax', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>NPI</label>
            <input type="text" value={form.ppeccNpi} onChange={e => set('ppeccNpi', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Street Address</label>
            <input type="text" value={form.ppeccAddress} onChange={e => set('ppeccAddress', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>City</label>
            <input type="text" value={form.ppeccCity} onChange={e => set('ppeccCity', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelClass}>State</label>
              <input type="text" maxLength={2} value={form.ppeccState} onChange={e => set('ppeccState', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>ZIP+4</label>
              <input type="text" value={form.ppeccZip} onChange={e => set('ppeccZip', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
          </div>
          <div className="md:col-span-2">
            <p className="text-sm font-medium text-gray-700 text-left">{F00028_PPECC_HOURS_LABEL}</p>
          </div>
          <div>
            <label className={labelClass}>Hours Open</label>
            <input type="time" value={form.ppeccOpen} onChange={e => set('ppeccOpen', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Hours Close</label>
            <input type="time" value={form.ppeccClose} onChange={e => set('ppeccClose', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label htmlFor="ppecc-timezone" className={labelClass}>Central Time / Mountain Time</label>
            <select
              id="ppecc-timezone"
              value={form.ppeccTimezone ?? ''}
              onChange={e => set('ppeccTimezone', e.target.value)}
              disabled={isReadOnly}
              className={inputClass}
            >
              <option value="">Select…</option>
              {F00028_PPECC_TIME_ZONES.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>
        </div>
      </CollapsibleSection>

      {/* Section D */}
      <CollapsibleSection title="Section D: Physician or Allowed Practitioner Information" sectionKey="physician-info" collapsed={!!collapsedSections['physician-info']} onToggle={() => toggleSection('physician-info')} colorClass="border-slate-200 bg-slate-50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Provider Name<RequiredMark /></label>
            <input type="text" value={form.physicianProviderName} onChange={e => set('physicianProviderName', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Telephone</label>
            <input type="tel" value={form.physicianPhone} onChange={e => set('physicianPhone', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>NPI<RequiredMark /></label>
            <input type="text" value={form.physicianNpi} onChange={e => set('physicianNpi', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>License Number</label>
            <input type="text" value={form.physicianLicense} onChange={e => set('physicianLicense', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>
      </CollapsibleSection>

      {/* Section E */}
      <CollapsibleSection title="Section E: Plan of Care" sectionKey="plan-of-care" collapsed={!!collapsedSections['plan-of-care']} onToggle={() => toggleSection('plan-of-care')} colorClass="border-amber-200 bg-amber-50">
        <div className="space-y-4">
          <div>
            <label className={labelClass}>Status<RequiredMark /></label>
            <div className="flex flex-wrap gap-4 mt-1">
              {['New Client', 'Extension', 'Revised Request'].map(opt => (
                <label key={opt} className="flex items-center gap-1.5 text-sm text-gray-700">
                  <input type="radio" name="pocStatus" value={opt} checked={form.pocStatus === opt} onChange={() => set('pocStatus', opt)} disabled={isReadOnly} />
                  {opt}
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Original SOC Date</label>
              <input type="date" value={form.originalSOCDate} onChange={e => set('originalSOCDate', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Revised Request Effective Date</label>
              <input type="date" value={form.revisedEffectiveDate} onChange={e => set('revisedEffectiveDate', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
          </div>

          <div>
            <label className={labelClass}>Services client receives from other agencies</label>
            <textarea rows={2} value={form.otherAgencyServices} onChange={e => set('otherAgencyServices', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>

          <div>
            <label className={labelClass}>Diagnoses<RequiredMark /></label>
            <textarea rows={3} value={form.diagnoses} onChange={e => set('diagnoses', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>

          <div>
            <label className={labelClass}>Functional Limitations / Permitted Activities / Homebound Status</label>
            <textarea rows={3} value={form.functionalLimitations} onChange={e => set('functionalLimitations', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>

          <div>
            <label className={labelClass}>Prescribed Medications</label>
            <textarea rows={3} value={form.medications} onChange={e => set('medications', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>

          <div>
            <label className={labelClass}>Diet Ordered</label>
            <textarea rows={2} value={form.dietOrdered} onChange={e => set('dietOrdered', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>

          <div>
            <label className={labelClass}>Mental Status</label>
            <textarea rows={2} value={form.mentalStatus} onChange={e => set('mentalStatus', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Prognosis</label>
              <select value={form.prognosis} onChange={e => set('prognosis', e.target.value)} disabled={isReadOnly} className={inputClass}>
                <option value="">Select...</option>
                <option value="Good">Good</option>
                <option value="Fair">Fair</option>
                <option value="Poor">Poor</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Rehabilitation Potential</label>
              <select value={form.rehabilitationPotential} onChange={e => set('rehabilitationPotential', e.target.value)} disabled={isReadOnly} className={inputClass}>
                <option value="">Select...</option>
                <option value="Good">Good</option>
                <option value="Fair">Fair</option>
                <option value="Poor">Poor</option>
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass}>Safety Precautions</label>
            <textarea rows={2} value={form.safetyPrecautions} onChange={e => set('safetyPrecautions', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>

          <div>
            <label className={labelClass}>Medical Necessity, Clinical Condition, Treatment Plan<RequiredMark /></label>
            <textarea rows={4} value={form.medicalNecessity} onChange={e => set('medicalNecessity', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>

          <div>
            <label className={labelClass}>HCPCS Service Requested<RequiredMark /></label>
            <input type="text" value={form.hcpcsRequested} onChange={e => set('hcpcsRequested', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Requested Dates of Service — From<RequiredMark /></label>
              <input type="date" value={form.dosFrom} onChange={e => set('dosFrom', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Requested Dates of Service — To<RequiredMark /></label>
              <input type="date" value={form.dosTo} onChange={e => set('dosTo', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>SN Quantity Visits Requested<RequiredMark /></label>
              <input type="number" min={0} value={form.snVisits} onChange={e => set('snVisits', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>HHA Quantity Visits Requested<RequiredMark /></label>
              <input type="number" min={0} value={form.hhaVisits} onChange={e => set('hhaVisits', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
          </div>

          <div>
            <label className={labelClass}>Supplies</label>
            <textarea rows={2} value={form.supplies} onChange={e => set('supplies', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>

          {/* DME Items sub-section */}
          <div className="pt-3 mt-2 border-t border-slate-200">
            <h5 className="text-sm font-semibold text-gray-700 mb-3">DME Items</h5>
            {(form.dmeItems as DMEItem[]).length === 0 && (
              <p className="text-sm text-gray-400 italic mb-3">No DME items added.</p>
            )}
            <div className="space-y-3">
              {(form.dmeItems as DMEItem[]).map((item, idx) => (
                <div key={idx} className="border border-slate-200 rounded-md p-3 relative">
                  {!isReadOnly && (
                    <button
                      type="button"
                      onClick={() => removeDMEItem(idx)}
                      className="absolute top-2 right-2 text-gray-400 hover:text-red-500 transition-colors"
                      aria-label="Remove DME item"
                    >
                      <X size={14} />
                    </button>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>Item Description</label>
                      <input type="text" value={item.description} onChange={e => setDME(idx, 'description', e.target.value)} readOnly={isReadOnly} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>How Long Needed</label>
                      <input type="text" value={item.howLong} onChange={e => setDME(idx, 'howLong', e.target.value)} readOnly={isReadOnly} className={inputClass} />
                    </div>
                    <div className="md:col-span-2">
                      <label className={labelClass}>Options</label>
                      <div className="flex gap-4">
                        {(['own', 'repair', 'buy', 'rent'] as (keyof DMEItem)[]).map(opt => (
                          <label key={opt} className="flex items-center gap-1.5 text-sm text-gray-700 capitalize">
                            <input
                              type="checkbox"
                              checked={!!item[opt]}
                              onChange={e => setDME(idx, opt, e.target.checked)}
                              disabled={isReadOnly}
                              className="rounded border-gray-300"
                            />
                            {opt}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {!isReadOnly && (
              <button
                type="button"
                onClick={addDMEItem}
                className="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-sm text-green-700 border border-green-400 rounded-md hover:bg-green-50 transition-colors"
              >
                <Plus size={14} />
                Add DME Item
              </button>
            )}
          </div>
        </div>
      </CollapsibleSection>

      {/* Signatures */}
      <CollapsibleSection title="Signatures" sectionKey="signatures" collapsed={!!collapsedSections['signatures']} onToggle={() => toggleSection('signatures')} colorClass="border-red-200 bg-red-50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <PrintedNameField signer="RN" value={form.rnSignature} onChange={v => set('rnSignature', v)} readOnly={isReadOnly} inputClassName={inputClass} labelClassName={labelClass} />
          </div>
          <div>
            <label className={labelClass}>Date Signed</label>
            <input type="date" value={form.rnDateSigned} onChange={e => set('rnDateSigned', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>

        <div className="mt-4 p-3 bg-gray-50 rounded-md text-sm text-gray-600 border border-gray-200">
          I anticipate home care will be required for the authorization period:
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <div>
            <label className={labelClass}>Authorization Period — From</label>
            <input type="date" value={form.authFrom} onChange={e => set('authFrom', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Authorization Period — To</label>
            <input type="date" value={form.authTo} onChange={e => set('authTo', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={!!form.conflictOfInterest}
              onChange={e => set('conflictOfInterest', e.target.checked)}
              disabled={isReadOnly}
              className="mt-0.5 rounded border-gray-300"
            />
            <span>I do not have a significant ownership interest in the services/supplies recommended above.</span>
          </label>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={!!form.exception}
              onChange={e => set('exception', e.target.checked)}
              disabled={isReadOnly}
              className="mt-0.5 rounded border-gray-300"
            />
            <span>Exception for governmental entities: I attest that the above disclosure requirement does not apply.</span>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <PrintedNameField signer="Physician" value={form.physicianSignature} onChange={v => set('physicianSignature', v)} readOnly={isReadOnly} inputClassName={inputClass} labelClassName={labelClass} />
          </div>
          <div>
            <label className={labelClass}>Date Signed</label>
            <input type="date" value={form.physicianDateSigned} onChange={e => set('physicianDateSigned', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>
      </CollapsibleSection>

      {/* Generate PDF moved to page action bar */}

      {actionError && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex justify-between items-start">
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className="ml-2 text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* Documents */}
      {formRecord && (
        <>
          <DocumentUpload
            documents={(formRecord.documents ?? []).map(d => ({
              id: d.id,
              name: d.name,
              size: d.size,
              uploadedAt: d.uploadedAt,
            }))}
            stagedFiles={stagedFiles}
            canUpload={formRecord.status === 'COMPLETE'}
            onFilesSelected={handleFilesSelected}
            onStagedRemove={handleRemoveStagedFile}
            onDownload={handleDownloadDocument}
            onDelete={handleDeleteDocument}
            uploading={uploading}
            deletingId={deletingDocId}
            collapsible={true}
            defaultOpen={true}
            maxSizeMb={7}
          />
          {stagedFiles.length > 0 && formRecord.status === 'COMPLETE' && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleUploadStagedFiles}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading...' : 'Upload Files'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HomeHealthPOCForm;
